<template>
  <div id="app" class="w-65 py-5 mx-auto">
    <div class="d-flex align-items-center text-darkblue">
      <a>Home</a>
      <p class="mx-2">/</p>
      <a>Samenstellen</a>
    </div>
    <div v-if="loading"  style="min-height: 100vh; height: 100vh; background-color: #fff0e1;">
      <p>Laden...</p>
    </div>
    <template v-else>
      <div class="sd-content-wrapper d-flex align-items-start w-100 mt-4">
        <div
          class="sd-content d-flex flex-column me-4"
          :style="
            !testProductDetail ? 'width: 65%;' : 'width: 100%; max-width: 100%;'
          "
        >
          <info-modal
            :showmodal="showModalData"
            :boxdata="boxdata"
            @showmodalevent="showModalEvent"
          />

          <!-- Conditionally render product-details or select-box -->
          <product-details
            v-if="testProductDetail"
            :boxes="boxes"
            :boxdata="boxdata"
            :testProductDetail="testProductDetail"
            @pickdate="pickdate"
            @cardtext="cardtext"
            @cardname="cardName"
            @pickcard="pickCard"
            @addresschange="updateReceiver"
            @another-box="addAnotherBox"
            :activeBox="activeBox"
          />
          <template v-else>
            <select-box
              :boxes="boxes"
              :pickedBox="boxdata.pickedBox"
              @pickbox="(box) => { handleBoxClick(); pickBox(box); }"
            />

            <div ref="selectFlavorsWrapper">
              <select-flavors
                :boxdata="boxdata"
                :flavors="boxdata?.pickedBox?.product_options?.smaken"
                @pickflavour="pickFlavour"
                @showmodal="showModalEvent"
                @removeflavour="removeFlavour"
                @update:flavours="boxdata.flavours = $event"
                @update:veganOnly="boxdata.veganOnly = $event"
              />
            </div>

            <div ref="cartPickerWrapper">
              <cart-picker
                @pickdate="pickdate"
                :boxdata="boxdata"
                :cards="cards"
                :previous-card-text="previousCardText"
                :is-editing="Boolean(editingItem)"
                @cardtext="cardtext"
                @cardname="cardName"
                @pickcard="pickCard"
                :testProductDetail="testProductDetail"
                @validate-box="handleBoxValidation"
                @validate-flavor="handleFlavorValidation"
                @card-error="handleCardError"
                @update:wantCard="boxdata.wantCard = $event"
                @update:selectedCard="boxdata.selectedCard = $event"
              />
            </div>

            <where-to
              :boxdata="boxdata"
              @addresschange="updateReceiver"
              @another-box="addAnotherBox"
              @datechange="pickdate"
              @checkout="handleCheckout"
              :testProductDetail="testProductDetail"
              :activeBox="activeBox"
            />
          </template>
        </div>
        <cart-component
          class="sd-cart ms-4"
          @checkout="handleCheckout"
          @edit-item="editItem"
          :editingItem="editingItem"
          :loading="checkoutLoading"
          :boxdata="boxdata"
          v-if="!testProductDetail"
        />
        <cart-component-mobile
          class="sd-cart-mobile ms-4"
          @checkout="handleCheckout"
          @edit-item="editItem"
          :editingItem="editingItem"
          :loading="checkoutLoading"
          :boxdata="boxdata"
          v-if="!testProductDetail"
        />
      </div>
    </template>
    <div v-if="checkoutLoading" class="checkout-status" role="status" aria-live="assertive">
      <div class="lds-dual-ring checkout-status__spinner" aria-hidden="true"></div>
      <div class="checkout-status__text">
        <p class="checkout-status__title">Momentje geduld...</p>
        <p class="checkout-status__message">
          We voegen je bestelling toe aan de winkelwagen en sturen je zo naar de checkout.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  reactive,
  ref,
  onMounted,
  computed,
  watch,
  nextTick,
  onBeforeUnmount,
} from "vue";
import CartComponent from "./components/cart-component.vue";
import CartComponentMobile from "./components/cart-component-mobile.vue";
import SelectBox from "./components/select-box.vue";
import SelectFlavors from "./components/select-flavors.vue";
import WhereTo from "./components/where-to.vue";
import CartPicker from "./components/cart-picker.vue";
import InfoModal from "./components/info-modal.vue";
import ProductDetails from "./components/product-details/product-details.vue"; // Correct import

import { useOrderStore } from "@/stores/orderStore";
import woocommerceService from "./woocommerceService";

// Initialize order store
const orderStore = useOrderStore();

// Reactive states
const editingItem = ref(null);
const testProductDetail = ref(false);
const showModalData = ref(false);
const loading = ref(true);
const checkoutLoading = ref(false);
const activeBox = ref({});
const boxError = ref(false);
const flavorError = ref(false);
const cardError = ref(false);
const isInitialLoad = ref(true);
const shouldScrollToFlavors = ref(false);
const previousCardText = ref("");

const normalizeSlug = (slug) => (slug || "").toString().toLowerCase();
const stripVariantSuffix = (slug) => normalizeSlug(slug).replace(/-(\d{1,2})$/, "");

const slugsMatch = (boxSlug, targetSlug) => {
  const normalizedBox = normalizeSlug(boxSlug);
  const normalizedTarget = normalizeSlug(targetSlug);

  if (!normalizedBox || !normalizedTarget) {
    return false;
  }

  if (normalizedBox === normalizedTarget) {
    return true;
  }

  const baseBoxSlug = stripVariantSuffix(normalizedBox);
  const baseTargetSlug = stripVariantSuffix(normalizedTarget);

  return (
    baseBoxSlug === normalizedTarget ||
    normalizedBox === baseTargetSlug ||
    baseBoxSlug === baseTargetSlug
  );
};

const findBoxBySlug = (boxList, targetSlug) => {
  if (!Array.isArray(boxList) || !targetSlug) {
    return null;
  }

  return boxList.find((box) => slugsMatch(box?.slug, targetSlug));
};

const resetBoxdata = (defaultBox = null) => {
  return {
    cartitemid: null,
    card: { id: null, text: "", name: "" },
    pickedBox: defaultBox,
    flavours: [],
    sendDate: new Date().setDate(new Date().getDate() + 1),
    receiver: {
      name: "",
      country: "Nederland",
      zipcode: "",
      number: "",
      addition: "",
      street: "",
      city: "",
    },
    wantCard: true,
    selectedCard: null,
    veganOnly: false,
  };
};

function clearOldStorageData() {
  try {
    if (localStorage.length > 0) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith("sweetdeliver-")) {
          if (key === "sweetdeliver-activeBox") continue;
          const item = JSON.parse(localStorage.getItem(key));
          if (Date.now() - item.timestamp > 7 * 24 * 60 * 60 * 1000) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
  } catch (e) {
    console.error("Error clearing old storage data:", e);
  }
}

const boxdata = reactive(resetBoxdata());

const boxes = reactive([]);
const cards = reactive([]);

const handleBoxClick = () => {
  if (!isInitialLoad.value) {
    shouldScrollToFlavors.value = true;
  }
};

const pickBox = async (box) => {  
  if (!box) {
    return;
  }

  // Store whether we're changing boxes
  const isChangingBox = boxdata.pickedBox?.id !== box.id;
  
  if (isChangingBox) {
    boxdata.flavours = [];
  }

  boxdata.pickedBox = { ...box };

  if (selectFlavorsWrapper.value && !isInitialLoad.value && shouldScrollToFlavors.value) {
    await nextTick();
    selectFlavorsWrapper.value.scrollIntoView({ behavior: "smooth" });
    const offset = -75;
    const elementPosition = selectFlavorsWrapper.value.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({
      top: elementPosition + offset,
      behavior: "smooth",
    });
    // Reset the flag after scrolling
    shouldScrollToFlavors.value = false;
  }
};

const pickFlavour = (flavour) => {
  if (boxdata.flavours.length < boxdata.pickedBox.size) {
    boxdata.flavours.push(flavour);
  }
};

const removeFlavour = (index) => {
  boxdata.flavours.splice(index, 1);
};

const updateReceiver = (receiverData) => {
  boxdata.receiver = receiverData;
};

const fetchBoxes = async () => {
  const urlPath = window.location.pathname;
  const urlParts = urlPath.split("/").filter((part) => part);
  const slug = urlParts[urlParts.length - 1] || "";

  const dataWithTimestamp = localStorage.getItem("sweetdeliver-boxesData");
  const cardsDataWithTimestamp = localStorage.getItem("sweetdeliver-kaartjesData");
  let cacheChecked = false;

  if (dataWithTimestamp && cardsDataWithTimestamp) {
    try {
      const { data, timestamp } = JSON.parse(dataWithTimestamp);
      const { data: cachedCardsData } = JSON.parse(cardsDataWithTimestamp);
      const timeNow = Date.now();

      if (timestamp && timeNow - timestamp < 60 * 60 * 1000) {
        cacheChecked = true;

        if (Array.isArray(data)) {
          boxes.splice(0, boxes.length, ...data);
        }

        if (Array.isArray(cachedCardsData)) {
          cards.splice(0, cards.length, ...cachedCardsData);
        }

        const cachedMatch = findBoxBySlug(data, slug);
        if (cachedMatch) {
          activeBox.value = cachedMatch;
          localStorage.setItem("sweetdeliver-activeBox", JSON.stringify(cachedMatch));

          if (urlParts.includes("brownieshop")) {
            pickBox(cachedMatch);
          }

          loading.value = false;
          return;
        }
      }
    } catch (error) {
      console.error("Error parsing cached boxes data:", error);
    }
  }

  try {
    const [response, activeBoxResponse, cardsResponse] =
      await woocommerceService.getProductsByMultipleCategories([32, 130, 129]);

    const responseWithSize = response.map((box) => ({
      ...box,
      size: Number(box.product_options?.aantal_smaken) || 0,
    }));

    const cardsData = cardsResponse.map((obj) => ({
      name: obj.name,
      id: obj.id,
      image: obj.images[0].src,
    }));

    boxes.splice(0, boxes.length, ...responseWithSize);
    cards.splice(0, cards.length, ...cardsData);

    let foundActiveBox = findBoxBySlug(responseWithSize, slug);

    if (!foundActiveBox && Array.isArray(activeBoxResponse)) {
      foundActiveBox = findBoxBySlug(activeBoxResponse, slug);
    }

    if (!foundActiveBox && boxes.length > 0) {
      const storedActiveBox = localStorage.getItem("sweetdeliver-activeBox");
      if (storedActiveBox) {
        try {
          const parsedActiveBox = JSON.parse(storedActiveBox);
          if (slugsMatch(parsedActiveBox?.slug, slug)) {
            foundActiveBox = parsedActiveBox;
          }
        } catch (error) {
          console.error("Error parsing cached active box:", error);
        }
      }
    }

    if (!foundActiveBox && boxes.length > 0 && !slug && cacheChecked) {
      foundActiveBox = boxes[0];
    }

    if (foundActiveBox) {
      activeBox.value = foundActiveBox;
      localStorage.setItem("sweetdeliver-activeBox", JSON.stringify(foundActiveBox));

      if (urlParts.includes("brownieshop")) {
        pickBox(foundActiveBox);
      }
    }

    try {
      const currentTime = Date.now();
      localStorage.setItem(
        "sweetdeliver-boxesData",
        JSON.stringify({
          data: responseWithSize,
          timestamp: currentTime,
        })
      );
      localStorage.setItem(
        "sweetdeliver-kaartjesData",
        JSON.stringify({
          data: cardsData,
          timestamp: currentTime,
        })
      );
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        clearOldStorageData();
        const currentTime = Date.now();
        localStorage.setItem(
          "sweetdeliver-boxesData",
          JSON.stringify({
            data: responseWithSize,
            timestamp: currentTime,
          })
        );
        localStorage.setItem(
          "sweetdeliver-kaartjesData",
          JSON.stringify({
            data: cardsData,
            timestamp: currentTime,
          })
        );
        if (foundActiveBox) {
          localStorage.setItem("sweetdeliver-activeBox", JSON.stringify(foundActiveBox));
        }
      } else {
        throw e;
      }
    }
  } catch (error) {
    console.error("Error fetching boxes:", error);
  } finally {
    loading.value = false;
  }
};

const showModalEvent = (flavour) => {
  showModalData.value = flavour;
};

const addAnotherBox = (boxDataFromWhereTo, reset = true) => {
  if (boxDataFromWhereTo) {
    previousCardText.value = boxDataFromWhereTo?.card?.text || "";

    if (editingItem.value) {
      Object.assign(editingItem.value, { ...boxDataFromWhereTo });
      editingItem.value = null;
    } else {
      orderStore.items.unshift({
        ...boxDataFromWhereTo,
        card: boxDataFromWhereTo.card,
        selectedCard: boxDataFromWhereTo.selectedCard,
      });
    }
    
    if (reset) {
      shouldScrollToFlavors.value = false;
      
      Object.assign(boxdata, resetBoxdata(boxes.length > 0 ? boxes[0] : null));
      
      setTimeout(() => {
        scrollToBoxes();
      }, 100);
    }
  }
};

const editItem = (item) => {
  Object.assign(boxdata, item);
  editingItem.value = item;
};

const formatDate = (date) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  date.setDate(date.getDate() + 1);
  return date.toISOString().split("T")[0];
};

const handleCheckout = async () => {
  checkoutLoading.value = true;
  const cartItems = orderStore.items.map((item) => ({
    product_id: item.pickedBox.id,
    quantity: Number.isFinite(Number(item?.quantity)) && Number(item.quantity) > 0
      ? Number(item.quantity)
      : 1,
    verzenddatum: formatDate(item.sendDate),
    delivery_address: {
      name: item.receiver.name,
      address_1: `${item.receiver.street} ${item.receiver.number}`,
      address_2: item.receiver.addition,
      city: item.receiver.city,
      postcode: item.receiver.zipcode,
      country: item.receiver.country,
    },
    smaken: item.flavours.map((flavour) => flavour.name),
    card: item.card.id
      ? {
          id: item.card.id,
          "kaart-tekst": item.card.text,
          "kaart-naam": item.card.name,
          "kaart-ontvanger": item.receiver.name,
        }
      : null,
  }));

  try {
    const response = await woocommerceService.addToCart(cartItems);
    if (response.success) {
      orderStore.clearItems();
      window.location.href = "/winkelwagen/";
    } else {
      throw new Error(response.message || "Failed to add items to cart");
    }
  } catch (error) {
    console.error("Error during checkout:", error);
  } finally {
    checkoutLoading.value = false;
  }
};

const cardtext = (text) => {
  boxdata.card.text = text;
};

const cardName = (name) => {
  boxdata.card.name = name;
};

const pickCard = (card) => {
  boxdata.card.id = card.id;
};

const pickdate = (date) => {
  boxdata.sendDate = date;
};

const flavoursSelected = computed(() => {
  return boxdata.flavours.length === (boxdata.pickedBox?.size || 0);
});

const selectFlavorsWrapper = ref(null);
const cartPickerWrapper = ref(null);

const handleBoxValidation = (isValid) => {
  boxError.value = !isValid;
  if (!isValid && !testProductDetail.value) {
    scrollToElement("select-box");
  }
};

const handleFlavorValidation = (isValid) => {
  flavorError.value = !isValid;
  if (!isValid && !testProductDetail.value) { 
    scrollToElement("select-flavors");
  }
};

const scrollToBoxes = () => {
  const selectBoxElement = document.getElementById('select-box');
  
  if (selectBoxElement) {
    selectBoxElement.scrollIntoView({ behavior: "smooth" });
    const offset = -200;
    const elementPosition = selectBoxElement.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({
      top: elementPosition + offset,
      behavior: "smooth",
    });
  } else {
    // Fallback: Try to find by class
    const boxesElement = document.querySelector('.sd-content');
    if (boxesElement) {
      boxesElement.scrollIntoView({ behavior: "smooth" });
      const offset = -75;
      const elementPosition = boxesElement.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition + offset,
        behavior: "smooth",
      });
    }
  }
};

const handleCardError = (isError) => {
  cardError.value = isError;
};

const scrollToElement = (elementId) => {
  if (testProductDetail.value) return;
  
  const element = document.getElementById(elementId);
  if (element) {
    const rect = element.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!isVisible) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }
};

watch(flavoursSelected, async (newVal) => {
  if (newVal && cartPickerWrapper.value && !testProductDetail.value && !isInitialLoad.value) {
    await nextTick();
    cartPickerWrapper.value.scrollIntoView({ behavior: "smooth" });
    const offset = -75;
    const elementPosition =
      cartPickerWrapper.value.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({
      top: elementPosition + offset,
      behavior: "smooth",
    });
  }
});

onMounted(async () => {
  clearOldStorageData();
  
  isInitialLoad.value = true;
  shouldScrollToFlavors.value = false;
  
  const originalScrollTo = window.scrollTo;
  const originalScrollBy = window.scrollBy;
  const originalElementScrollIntoView = Element.prototype.scrollIntoView;
  
  window.scrollTo = function() { };
  window.scrollBy = function() { };
  Element.prototype.scrollIntoView = function() { };
  
  await fetchBoxes();

  const productCategories = window.currentProductCategory || [];
  testProductDetail.value = !productCategories.includes(32);

  // Comment out this for product details layout
  // if (!boxdata.pickedBox && boxes.length > 0) {
  //     pickBox(boxes[0]);
  //   }
  //   testProductDetail.value = false;

  if (!testProductDetail.value) {
    window.addEventListener("scroll", handleScroll);
  }
  
  setTimeout(() => {
    window.scrollTo = originalScrollTo;
    window.scrollBy = originalScrollBy;
    Element.prototype.scrollIntoView = originalElementScrollIntoView;
    
    setTimeout(() => {
      isInitialLoad.value = false;
    }, 500);
  }, 500);
});

onBeforeUnmount(() => {
  if (!testProductDetail.value) {
    window.removeEventListener("scroll", handleScroll);
  }
});

const handleScroll = () => {
  if (testProductDetail.value) return;
  
  const flavorsElement = document.getElementById("select-flavors");
  if (flavorsElement) {
    const rect = flavorsElement.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!isVisible && boxdata.flavours.length < boxdata.pickedBox.size) {
      flavorError.value = true;
    }
  }
};
</script>

<style>
body {
  overflow-x: hidden;
}

.sd-content {
  max-width: 75%;
  width: 75%;
}

div.loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.5);
  z-index: 500;
  margin: auto;
}

.w-65 {
  width: 80%;
  max-width: 1240px;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 768px) {
  .w-65 {
    min-width: 92%;
  }
}

@media (max-width: 1400px) {
  .w-65 {
    width: 95%;
  }
}

@media (max-width: 1000px) {
  .w-65 {
    width: 80%;
  }
}

.sd-cart-mobile {
  display: none !important;
}

@media (max-width: 1000px) {
  .sd-content-wrapper .sd-content {
    min-width: 100%;
  }

  .sd-cart {
    display: none !important;
  }

  .sd-cart-mobile {
    display: block !important;
  }
}

p {
  margin-bottom: 0px;
}

.bg-darkblue {
  background-color: #152e88;
}

.bg-red {
  background-color: rgba(255, 74, 74, 1);
}

.border-red {
  border: 1px solid rgba(255, 74, 74, 1);
}

.text-red {
  color: rgba(255, 74, 74, 1);
}

.bg-red:hover {
  background-color: rgba(255, 74, 74, 1);
}

.bg-pink {
  background-color: #ffb8b3;
}

.fs-15 {
  font-size: 15px;
}

.fw-medium {
  font-weight: 500 !important;
}

.text-pink {
  color: #ffb8b3;
}

.p-4-5 {
  padding: 2.2rem;
}

.pt-4-5 {
  padding-top: 2.2rem;
}

.pe-4-5 {
  padding-right: 2.2rem;
}

.ps-4-5 {
  padding-left: 2.2rem;
}

.text-darkblue {
  color: #152e88;
}

.text-blue {
  color: #395ad4;
}

.lds-dual-ring {
  display: inline-block;
  width: 80px;
  height: 80px;
}
.lds-dual-ring:after {
  content: " ";
  display: block;
  width: 64px;
  height: 64px;
  margin: 8px;
  border-radius: 50%;
  border: 6px solid #fff;
  border-color: #ff4a4a transparent;
  animation: lds-dual-ring 1.2s linear infinite;
}
@keyframes lds-dual-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.checkout-status {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 3000;
  background-color: #ffffff;
  border-radius: 16px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 320px;
  border: 1px solid rgba(255, 74, 74, 0.15);
}

.checkout-status__title {
  font-size: 1rem;
  font-weight: 600;
  color: #152e88;
}

.checkout-status__message {
  font-size: 0.9rem;
  color: #4f4f4f;
  margin: 0;
}

.checkout-status__spinner {
  width: 40px;
  height: 40px;
}

.checkout-status__spinner:after {
  width: 28px;
  height: 28px;
  margin: 6px;
  border-width: 4px;
  border-color: #ff4a4a transparent #ff4a4a transparent;
}
#app {
  margin-top: 20px;
  background-color: #fff0e1 !important;
}

body {
  background-color: #fff1e3;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  text-transform: uppercase;
  letter-spacing: -1px;
  font-size: 26px !important;
}

h5 {
  font-weight: 400;
}

h1 span,
h2 span,
h3 span,
h4 span,
h5 span,
h6 span {
  color: #ff4a4a;
}

p,
a,
button,
li {
  font-family: "DMSans-Regular";
}

button.sd-btn {
  fill: #ffffff;
  color: #ffffff;
  background-color: #ff4a4a;
  border-radius: 5px;
  padding: 20px 40px;
  border: none;
  font-size: 15px;
  transition: all 0.4s;
}

button.sd-btn:hover {
  background-color: #1a36c8;
}

button.sd-btn.yellow {
  background-color: #fff1e3;
  color: #000;
}

button.sd-btn.yellow:hover {
  background-color: #ff4a4a;
  color: #fff;
}

button.sd-btn.grey {
  background-color: #d9cdc1;
  color: #000;
}

button.sd-btn.grey:hover {
  background-color: #ff4a4a;
  color: #fff;
}

button.sd-btn.blue {
  background-color: #1a36c8;
  color: #fff;
}

button.sd-btn.blue:hover {
  background-color: #ff4a4a;
  color: #fff;
}

.scrollOffset {
  height: 20px;
  visibility: hidden;
  margin-top: -180px;
  position: absolute;
}

.fs-15 {
  font-size: 15px;
}

.text-red {
  color: #ff4a4a;
}

.text-red:hover {
  color: rgba(255, 74, 74, 0.8);
}
</style>
